import { ProofTypeEnum } from 'src/ts/enums';
import { ProofType } from 'src/ts/interfaces';

export const NAME = 'assetVerification';

// The four proof types VOA reviews: FVS's EXTRACTABLE_VOA_DOCUMENTS (bank
// statement) plus AUDIT_ONLY_VOA_DOCUMENTS. Paystubs and offer letters are never
// part of an asset review.
export const VOA_ASSET_PROOF_TYPES: ReadonlyArray<ProofType> = [
  ProofTypeEnum.BankStatement,
  ProofTypeEnum.CashAppStatement,
  ProofTypeEnum.CreditDebitCardStatement,
  ProofTypeEnum.InvestmentAccount
];

// FVS accountType enum per the integration contracts page (2437283873, "Shared
// structures") — wire values with display labels. The Figma dropdown (23:4919)
// was a pre-contract draft; an empty selection maps to null, the contract's
// catch-all for unclassifiable accounts.
export const ACCOUNT_TYPE_OPTIONS = [
  { value: 'PERSONAL_CHECKING', label: 'Personal checking' },
  { value: 'PERSONAL_SAVINGS', label: 'Personal savings' },
  { value: 'BUSINESS_CHECKING', label: 'Business checking' },
  { value: 'BUSINESS_SAVINGS', label: 'Business savings' },
  { value: 'INVESTMENT', label: 'Investment' },
  { value: 'CREDIT_DEBIT_CARD', label: 'Credit/debit card' },
  { value: 'CASH_APP', label: 'Cash app' }
] as const;
export type AccountTypeOption = (typeof ACCOUNT_TYPE_OPTIONS)[number]['value'];

export const accountTypeLabel = (value: string): string =>
  ACCOUNT_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value;

// FVS ReviewCompleteRequest caps both arrays at 50 (availability control on the
// row-locked completion transaction) — the add buttons stop before the wire refuses.
export const MAX_ACCOUNTS = 50;
export const MAX_STATEMENTS_PER_ACCOUNT = 50;

// Statement reason codes with special affordances. 2003 (name mismatch)
// never excludes the statement from FVS's mean, so resolving it changes nothing.
// 2024 (duplicate calendar start date) is server-added: FVS re-derives it on every
// recompute, so stripping it client-side is pointless.
// 2023 (stale start date) is derived client-side from the saved start date, so
// Resolve would strip it only until the next edit re-attached it; it gets the
// Override affordance instead.
export const VOA_REASON_CODE_NAME_MISMATCH = 2003;
export const VOA_REASON_CODE_DUPLICATE_START_DATE = 2024;
export const VOA_REASON_CODE_STALE_START = 2023;

export const VOA_STALE_START_WARNING =
  'This statement is older than 90 days and will not be included.';

export const isResolvableReasonCode = (code: number): boolean =>
  code !== VOA_REASON_CODE_NAME_MISMATCH &&
  code !== VOA_REASON_CODE_DUPLICATE_START_DATE &&
  code !== VOA_REASON_CODE_STALE_START;

// Mirrors FVS hasNoExcludingReasonCodes (financial-verification-services
// totalBankStatementResults.ts:97-99): every code except 2003 keeps the statement
// out of the account mean, warnings (20xx) exactly as much as errors (30xx). Keep
// this in step with that function or the review gates on rules FVS does not apply.
export const isExcludingReasonCode = (code: number): boolean =>
  code !== VOA_REASON_CODE_NAME_MISMATCH;

// Codes an analyst may attach to reject a statement. 2003 is absent: it never
// excludes, so offering it would be a rejection that does nothing.
export const VOA_REJECTION_REASON_CODES = [
  2020, 2021, 2022, 2023, 2024, 2025, 2027, 3020, 3021, 3022, 3024, 3025, 3026, 3027
] as const;

// FVS never sends copy: its StatusDetail is `{ code }` alone
// (financial-verification-services src/.../asset-verification/types.ts:168), and the
// optional `error` string only ever arrives from FP's own simulated fixtures. Without
// this map a real review renders "Reason code 2023" on a statement and drops
// review-level warnings entirely, so the labels below are the analyst-facing source of
// truth. Keyed to AssetVerificationStatusCode (same file, :138) — keep them in step.
const VOA_REASON_CODE_LABELS: Record<number, string> = {
  // 2003 and 3022 are the same topic at different severities
  // (WarningOneOrMoreAccountHolderNameMismatch / ErrorOneOrMore…), so their
  // labels must stay distinct or the review renders one sentence twice.
  2003: 'Account holder name mismatch on one or more statements (warning)',
  2020: 'Ending balance missing on one or more statements',
  2021: 'Start date missing on one or more statements',
  2022: 'Institution name missing on one or more statements',
  2023: 'One or more statements start more than 90 days ago',
  2024: 'One or more statements in this account are duplicates',
  2025: 'One or more statements are for a non-personal account',
  2027: 'One or more statements are not eligible for verification',
  3020: 'No ending balance could be read from these statements',
  3021: 'Account holder name missing on one or more statements',
  3022: 'Account holder name mismatch on one or more statements (error)',
  3024: 'Every statement starts more than 90 days ago',
  3025: 'Every statement is missing its start date',
  3026: 'Every statement is for a non-personal account',
  3027: 'No statement is eligible for verification',
  404: 'The document could not be retrieved',
  500: 'The document could not be processed'
};

// An unmapped code still shows its number rather than nothing: a new FVS code should
// read as unfamiliar, not disappear.
export const reasonCodeLabel = ({ code, error }: { code: number; error?: string | null }): string =>
  error ?? VOA_REASON_CODE_LABELS[code] ?? `Reason code ${code}`;

export const VOA_STORAGE_PREFIX = 'voa-review';
// Bump on ANY change to the persisted ReviewState shape — additive too. A
// mismatched envelope is discarded, which is always safer than a half-shaped restore.
// v2: PanelAccount lost documentId (accounts are review-global per contract v13).
// v4: PanelStatement gained analystReasonCodes.
// v5: PanelStatement gained ninetyDayOverridden; account identity dropped last 4.
// v6: documents now come from the entry's asset proofs, not the FVS record.
export const VOA_STORAGE_VERSION = 6;
